import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CreditCard, TrendingUp, Shield, Users, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const BusinessAccounts = () => {
  const accountTypes = [
    {
      icon: Building2,
      title: "Business Checking",
      description: "Manage daily operations with unlimited transactions",
      features: ["No monthly fees", "Unlimited transactions", "Online & mobile banking", "Free business debit card"]
    },
    {
      icon: TrendingUp,
      title: "Business Savings",
      description: "Earn interest while maintaining liquidity",
      features: ["Competitive interest rates", "Easy transfers", "FDIC insured", "No minimum balance"]
    },
    {
      icon: CreditCard,
      title: "Merchant Services",
      description: "Accept payments from customers seamlessly",
      features: ["Credit card processing", "POS systems", "Online payment gateway", "Next-day funding"]
    },
    {
      icon: Shield,
      title: "Business Credit Cards",
      description: "Flexible financing for business expenses",
      features: ["Rewards program", "Employee cards", "Expense tracking", "No annual fee"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-indigo-600 to-indigo-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Business Accounts</h1>
            <p className="text-lg sm:text-xl text-indigo-100 max-w-3xl mx-auto mb-8">
              Banking solutions designed to help your business thrive, from startups to established enterprises
            </p>
            <Button asChild className="bg-white text-indigo-600 hover:bg-indigo-50">
              <Link to="/contact">Open Business Account <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Account Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {accountTypes.map((account, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                    <account.icon className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{account.title}</h3>
                  <p className="text-gray-600 mb-6">{account.description}</p>
                  <ul className="space-y-2">
                    {account.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-indigo-600 rounded-full mr-3" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">Why Businesses Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Zap className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Fast Setup</h3>
              <p className="text-gray-600">Open your business account in minutes, not days</p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Dedicated Support</h3>
              <p className="text-gray-600">Personal relationship manager for your business</p>
            </div>
            <div className="text-center">
              <Shield className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Advanced Security</h3>
              <p className="text-gray-600">Fraud protection and account monitoring 24/7</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Grow Your Business?</h2>
          <p className="text-lg sm:text-xl text-indigo-100 mb-8">
            Join thousands of businesses that trust Morgan Guaranty Trust
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-indigo-700">
              <Link to="/user/register">Open Account</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessAccounts;
